// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { isLikelyVaultId } from '../api.js';
import { siblingVerifierOrigin } from '../verifier-sibling-origins.js';

interface HomeProps {
  /** The origin this bundle is served from, used to resolve the sibling
   *  shared-vault verifier for the cross-link. Defaults to
   *  `window.location.origin`. Exposed for tests — production callers should
   *  rely on the default. */
  ownOrigin?: string;
}

export function Home({ ownOrigin }: HomeProps) {
  const location = useLocation();
  const [vaultId, setVaultId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // COB-507 rider: point visitors who want the OTHER surface at it. Resolves
  // through the hard-coded sibling map, so an unmapped origin renders nothing
  // rather than guessing a hostname.
  const sharedVerifier = siblingVerifierOrigin(
    ownOrigin ?? (typeof window !== 'undefined' ? window.location.origin : ''),
  );

  function onSubmit(e: Event) {
    e.preventDefault();
    const trimmed = vaultId.trim();
    if (!isLikelyVaultId(trimmed)) {
      setError('That does not look like a vault ID. It should be a UUID.');
      return;
    }
    setError(null);
    location.route(`/v/${trimmed}`);
  }

  return (
    <>
      <section class="home-hero">
        <h1 class="home-title">Look up a vault</h1>
        <p class="home-subtitle">
          Enter a vault ID to see what the independent verifier knows
          about it: the contract it accepted, the keys it holds, and
          the lifecycle state from the verifier's point of view.
        </p>
      </section>

      <form class="home-form" onSubmit={onSubmit}>
        <label class="visually-hidden" for="vault-id-input">
          Vault ID
        </label>
        <input
          id="vault-id-input"
          class="home-input"
          type="text"
          autocomplete="off"
          spellcheck={false}
          placeholder="00000000-0000-0000-0000-000000000000"
          value={vaultId}
          onInput={(e) => setVaultId((e.target as HTMLInputElement).value)}
        />
        {error && <div class="home-error" role="alert">{error}</div>}
        <div class="home-form-actions">
          <button type="submit" class="btn btn-primary">
            Look up
          </button>
        </div>
        {sharedVerifier && (
          <p class="home-crosslink">
            This page looks up strategy vaults. Reviewing a shared vault?{' '}
            <a href={`${sharedVerifier}/`}>Use the shared-vault verifier →</a>
          </p>
        )}
      </form>

      <aside class="home-explainer">
        <h2>Why this exists</h2>
        <p>
          Cobault vaults are a 2-of-3 multisig with one independent
          verifier key. This page is the verifier's side of the trust
          story: anything you can see here is what the verifier itself
          will use when deciding whether to co-sign a withdrawal. If
          your user-app shows a different contract hash than this page
          does, refuse to transact and contact support.
        </p>
      </aside>
    </>
  );
}
